/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import { Controller, useWatch } from "react-hook-form";
import api from "../../../api/axios";
import SetCountyModal from "../popups/SetCountyModal";
import usePlaceAutocomplete from "../hooks/usePlaceAutocomplete";
import FloatingInput from "../../../components/input/FloatingInput";

export default function GeneralInformationSection({
  control,
  getValues,
  setValue,
  user,
  openModal,
  closeModal,
  showToaster,
  onSalesPriceBlur,
  onAddressEnriched,
  onZipVerified,
  setCompanyId,
  mode = "seller",
}) {
  // State Management
  const [showUnit, setShowUnit] = useState(
    !!getValues("addressMeta.addressIncludesUnit"),
  );
  const mountedRef = useRef(true);

  const unsupportedZipMessage =
    "The zip code you entered is incorrect or the State/County is not supported.";

  // Helper Functions
  const ucWords = (str = "") =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const fullName = ucWords(
    `${user?.first_name ?? ""} ${user?.last_name ?? ""}`,
  ).trim();

  const getVal = useCallback(
    (path, fallback = "") => {
      try {
        if (typeof getValues === "function") {
          const v = getValues(path);
          return v === undefined ? fallback : v;
        }
      } catch (e) {
        return fallback;
      }
      return fallback;
    },
    [getValues],
  );

  // Initialize prepared by name
  useEffect(() => {
    if (user && (user?.first_name || user?.last_name)) {
      setValue("preparedBy", fullName);
    }
  }, [user, setValue, fullName]);

  // Sync showUnit with form data
  useEffect(() => {
    setShowUnit(!!getValues("addressMeta.addressIncludesUnit"));
  }, [getValues]);

  // Mounted ref cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Apply address payload to form
  const applyPayload = useCallback(
    async (payload) => {
      const oldmrsid = getValues("addressMeta")?.mrsid || "";
      const timestamp = Date.now();
      if (!payload) return;
      try {
        const stateCode =
          payload.state ||
          (payload.enrichment && payload.enrichment.state_code) ||
          "";
        const countyName =
          (payload.enrichment &&
            (payload.enrichment.county_name ||
              (payload.enrichment.data &&
                payload.enrichment.data.county_name))) ||
          "";
        const stateid =
          (payload.enrichment && payload.enrichment.stateid) || "";
        const countyid =
          (payload.enrichment && payload.enrichment.countyid) || "";
        const site_unit = payload.site_unit || "";
        _setQuery(payload.formatted_address || "");

        const prev =
          typeof getValues === "function" ? getValues("addressMeta") || {} : {};
        const formattedAddress = payload.formatted_address || "";
        const streetNo = payload.street_no || "";
        const route = payload.route || payload.route_long || "";
        const raw = payload.enrichment || null;

        const stateName =
          raw?.state_name || raw?.data?.state_name || stateCode || "";
        const closingCounty =
          raw?.closing_county ||
          raw?.data?.closing_county ||
          raw?.county_name ||
          raw?.data?.county_name ||
          countyName ||
          "";
        const countyNorm =
          raw?.county_name || raw?.data?.county_name || countyName || "";
        const cityName =
          payload.city || raw?.city || raw?.data?.city || prev?.city || "";
        const ttb = raw && raw.ttb ? raw.ttb : raw || null;
        // `prev` (the address already in the form) must be the LAST resort here, not
        // checked ahead of this response's own resolved id — `ttb` often ends up being
        // zipData.ttb (property/tax details, no stateid/countyid of its own) rather than
        // zipData itself, so it was previously falling through straight to the stale
        // `prev` value and never reaching the `stateid`/`countyid` this response actually
        // resolved (computed above from payload.enrichment, which usePlaceAutocomplete.js
        // sets as a flat spread of zipData) — silently keeping whatever state/county was
        // already in the form instead of the new address's.
        const countyidComputed =
          (ttb && ttb.countyid) ||
          payload?.countyid ||
          countyid ||
          prev?.countyid ||
          "";
        const stateidComputed =
          (ttb && ttb.stateid) ||
          payload?.stateid ||
          stateid ||
          prev?.stateid ||
          "";
        const siteUnit = site_unit || prev?.addressIncludesUnit || "";

        const resolvedCompanyId =
          payload?.companyid || raw?.companyid || raw?.data?.companyid || "";

        try {
          setValue(
            "addressMeta",
            {
              ...(prev || {}),
              companyid: resolvedCompanyId || prev?.companyid || "",
              address: formattedAddress,
              street_no: streetNo,
              street_name: route,
              closing_state:
                stateName || prev?.closing_state || prev?.state || "",
              closing_county:
                closingCounty || prev?.closing_county || prev?.county || "",
              county: countyNorm || prev?.county || "",
              city: cityName,
              state: stateCode || prev?.state || "",
              countyid: countyidComputed,
              stateid: stateidComputed,
              zip: payload?.zip || prev?.zip || "",
              addressIncludesUnit: siteUnit,
              unitNumber: siteUnit || prev?.unitNumber || "",
              mrsid: oldmrsid ? oldmrsid : `${timestamp}`,
            },
            { shouldDirty: true, shouldTouch: true },
          );
        } catch (e) {
          console.warn("Failed to set addressMeta in RHF", e);
        }

        if (mode === "buyer") {
          if (raw || formattedAddress) {
            try {
              setValue("addressVerified", true, {
                shouldDirty: true,
                shouldTouch: true,
              });
            } catch (e) {
              console.warn("Failed to set addressVerified", e);
            }
          }
          if (typeof setCompanyId === "function") {
            const cid = String(resolvedCompanyId || "").trim();
            if (cid) {
              const n = Number(cid);
              setCompanyId(Number.isFinite(n) ? n : cid);
            }
          }
        }

        // Set annual property tax amount if available
        const taxAmount =
          payload.enrichment?.taxAmount ||
          payload.enrichment?.ttb?.taxAmount ||
          payload.enrichment?.data?.ttb?.taxAmount ||
          null;

        if (taxAmount !== null && taxAmount !== undefined) {
          if (mode === "seller") {
            setValue &&
              setValue("loanInfoMeta.annualPropertyTax", taxAmount, {
                shouldDirty: true,
                shouldTouch: true,
              });
            setValue &&
              setValue("loanInfoMeta.annualPropertyTaxAmount", taxAmount, {
                shouldDirty: true,
                shouldTouch: true,
              });
          } else if (mode === "buyer") {
            setValue &&
              setValue("loanInfoMeta.annualPropertyTax", taxAmount, {
                shouldDirty: true,
                shouldTouch: true,
              });
          }
        }

        if (payload.loanBalance && mode === "seller") {
          try {
            setValue &&
              setValue("loanInfoMeta.autoEstimateLoan", true, {
                shouldDirty: true,
                shouldTouch: true,
              });
            if (
              payload.loanBalance.balance !== undefined &&
              payload.loanBalance.balance !== null
            ) {
              setValue &&
                setValue(
                  "loanInfoMeta.loansToBePaidBalance1",
                  payload.loanBalance.balance,
                  { shouldDirty: true, shouldTouch: true },
                );
            }
            if (
              payload.loanBalance.interestRate !== undefined &&
              payload.loanBalance.interestRate !== null
            ) {
              setValue &&
                setValue(
                  "loanInfoMeta.loansToBePaidRate1",
                  payload.loanBalance.interestRate,
                  { shouldDirty: true, shouldTouch: true },
                );
            }
          } catch (e) {
            console.warn("Failed to write loan balance into RHF", e);
          }
        }
      } catch (e) {
        console.warn("applyPayload failed", e);
      }

      try {
        const rawData = payload?.enrichment || null;

      //  if (mode === "seller" && typeof onAddressEnriched === "function") {
          const ttb =
            payload?.loanBalance || rawData?.ttb
              ? { ...(rawData?.ttb || {}), loanBalance: payload?.loanBalance }
              : rawData?.ttb || null;
      //  }
          // RHF already has stateid/county from setValue above; pass through so
          // handleAddressEnriched does not overwrite them with empty payload.* fields.
          const addrSnap =
            typeof getValues === "function" ? getValues("addressMeta") || {} : {};

          onAddressEnriched({
            ttb,
            payload: {
              ...payload,
              companyid:
                payload?.companyid ||
                rawData?.companyid ||
                rawData?.data?.companyid ||
                addrSnap.companyid ||
                "",
              state_code: payload?.state || rawData?.state_code || "",
              stateid:
                addrSnap.stateid ||
                rawData?.stateid ||
                rawData?.data?.stateid ||
                rawData?.ttb?.stateid ||
                "",
              countyid:
                addrSnap.countyid ||
                rawData?.countyid ||
                rawData?.data?.countyid ||
                rawData?.ttb?.countyid ||
                "",
              closing_county:
                addrSnap.closing_county ||
                rawData?.closing_county ||
                rawData?.data?.closing_county ||
                "",
              closing_state:
                addrSnap.closing_state ||
                rawData?.closing_state ||
                rawData?.data?.closing_state ||
                "",
              county_name:
                addrSnap.county ||
                rawData?.county_name ||
                rawData?.data?.county_name ||
                "",
              zip: payload?.zip || addrSnap.zip || "",
            },
            raw: rawData,
            showPreview: false,
          });
        } catch (err) {
          console.warn("Failed to notify onAddressEnriched", err);
        }
    },
    [
      getValues,
      setValue,
      showToaster,
      onSalesPriceBlur,
      mode,
      onAddressEnriched,
      setCompanyId,
    ],
  );

  // Autocomplete Hook
  const {
    query,
    setQuery: setQueryFromHook,
    suggestions,
    suggestionItemRefs,
    handleChange,
    handleKeyDown,
    handleBlur,
    selectSuggestion,
  } = usePlaceAutocomplete({
    country: "us",
    onSelect: applyPayload,
    showToaster,
    mode: mode,
  });

  const _setQuery = setQueryFromHook;

  // Watch address fields
  const watchedCity = useWatch({ control, name: "addressMeta.city" });
  const watchedState = useWatch({ control, name: "addressMeta.state" });
  const watchedZip = useWatch({ control, name: "addressMeta.zip" });
  const watchedCounty = useWatch({
    control,
    name: "addressMeta.closing_county",
  });
  const watchedAddressIncludesUnit = useWatch({
    control,
    name: "addressMeta.addressIncludesUnit",
  });

  // Verify ZIP Code
  const verifyZip = useCallback(
    async (z) => {
      try {
        const res = await api.get(
          `/get-city-state-for-zip?zip=${encodeURIComponent(z)}`,
        );

        if (res?.status === 400) {
          if (typeof showToaster === "function") {
            try {
              showToaster({
                message: unsupportedZipMessage,
                visible: true,
                type: "error",
              });
            } catch (e) {
              /* ignore */
            }
          }
          return;
        }

        const body = res?.data;
        if (!mountedRef.current) return;

        if (body && body.status === "success" && body.data) {
          const d = body.data || {};
          try {
            const prev =
              typeof getValues === "function"
                ? getValues("addressMeta") || {}
                : {};

            // This is a bare zip-only lookup (no street/city context), fired on the
            // standalone Zip field's blur. If the user already resolved a full street
            // address for this exact zip (via the address autocomplete, which does have
            // that context and is authoritative), don't let this less-precise lookup
            // clobber its stateid/county/companyid — that's the race that was overwriting
            // a correct stateid (e.g. 29) with a wrong one (e.g. 5) after the fact.
            if (
              String(prev?.zip || "") === String(z || "") &&
              prev?.street_no &&
              prev?.city
            ) {
              return;
            }

            const updated = {
              ...(prev || {}),
              state: d.state_code || prev?.state || "",
              city: d.city || prev?.city || "",
              county: d.county || prev?.county || "",
              closing_county:
                (d.county_name ?? prev?.closing_county) ||
                prev?.county_name ||
                "",
              stateid: (d.stateid ?? prev?.stateid) || "",
              companyid:
                d.companyid != null && d.companyid !== ""
                  ? d.companyid
                  : prev?.companyid || "",
            };

            setValue &&
              setValue("addressMeta", updated, {
                shouldDirty: true,
                shouldTouch: true,
              });
            if (
              mode === "buyer" &&
              typeof setCompanyId === "function" &&
              d.companyid != null &&
              String(d.companyid).trim() !== ""
            ) {
              const cid = String(d.companyid).trim();
              const n = Number(cid);
              setCompanyId(Number.isFinite(n) ? n : cid);
            }
            if (mode === "seller" && typeof onZipVerified === "function") {
              onZipVerified({ stateid: updated.stateid });
            }
          } catch (e) {
            console.warn("verifyZip: failed to set addressMeta", e);
          }
        }
      } catch (err) {
        if (err && err.response && err.response.status === 400) {
          if (typeof showToaster === "function") {
            try {
              showToaster({
                message: unsupportedZipMessage,
                visible: true,
                type: "error",
              });
            } catch (e) {
              /* ignore */
            }
          }
        }
      }
    },
    [getValues, setValue, showToaster, onSalesPriceBlur, onZipVerified, mode, setCompanyId],
  );

  const handleSelect = useCallback(
    async (item) => {
      try {
        await selectSuggestion(item);
      } catch (e) {
        console.warn("selectSuggestion failed", e);
      }
    },
    [selectSuggestion],
  );

  // Prepared for handlers
  const handlePreparedForFocus = (field) => (e) => {
    if ((field.value ?? "").toString().trim() === "New Client") {
      setValue("preparedFor", "", { shouldDirty: true, shouldTouch: true });
    }
    field.onFocus?.(e);
  };

  const handlePreparedForBlur = (field) => (e) => {
    const val = e.target?.value ?? field.value;
    const trimmed = (val ?? "").toString().trim();
    setValue("preparedFor", trimmed || "New Client", {
      shouldDirty: true,
      shouldTouch: true,
    });
    field.onBlur?.(e);
  };

  return (
    <>
      <h3 className="m-btm-16">
        General information <span className="heading-e">Recommended input</span>
      </h3>

      {/* Prepared By and Prepared For */}
      <div className="calc-frm-field full-dvd">
        <div className="calc-frm-fld form-group input-sticky">
          <Controller
            name="preparedBy"
            control={control}
            defaultValue={getVal("preparedBy", "")}
            render={({ field }) => (
              <FloatingInput
                {...field}
                placeholder=""
                label="Prepared by"
                type="text"
              />
            )}
          />
        </div>
        <div className="calc-frm-fld form-group input-sticky">
          <Controller
            name="preparedFor"
            control={control}
            defaultValue={getVal("preparedFor", "New Client")}
            render={({ field }) => (
              <FloatingInput
                {...field}
                placeholder=""
                label="Prepared for"
                type="text"
                onFocus={handlePreparedForFocus(field)}
                onBlur={handlePreparedForBlur(field)}
              />
            )}
          />
        </div>
      </div>

      {/* Address Includes Unit Checkbox */}
      <div className="calc-frm-field full-dvd fsapce-top fsapce-bottom">
        <div className="calc-frm-fld form-group checkbox-fld">
          <Controller
            name="addressMeta.addressIncludesUnit"
            control={control}
            defaultValue={
              !!(getValues
                ? getValues("addressMeta.addressIncludesUnit")
                : false)
            }
            render={({ field }) => (
              <p>
                <input
                  type="checkbox"
                  checked={!!field.value}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    field.onChange(checked);
                    setShowUnit(checked);
                  }}
                />{" "}
                Address includes a Unit
              </p>
            )}
          />
        </div>
        <div className="calc-frm-fld form-group"></div>
      </div>

      {/* Address Input with Autocomplete */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group input-sticky">
          <Controller
            name="addressMeta.address"
            control={control}
            defaultValue={
              getValues ? (getValues("addressMeta.address") ?? "") : ""
            }
            render={({ field }) => (
              <FloatingInput
                {...field}
                placeholder="Enter a location"
                label="Address"
                type="text"
                className="input-filled"
                value={query || field.value || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v);
                  handleChange(v);
                  _setQuery(v);
                }}
                onKeyDown={(e) => {
                  handleKeyDown(e);
                }}
                onBlur={() => {
                  handleBlur();
                  try {
                    field.onChange(query || field.value || "");
                  } catch (e) {
                    /* ignore */
                  }
                }}
              />
            )}
          />

          {suggestions && suggestions.length > 0 && (
            <div className="search-list-prop">
              {suggestions.map((s, idx) => (
                <div
                  key={idx}
                  ref={(el) => (suggestionItemRefs.current[idx] = el)}
                  className="search-list-prop-wrap"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(s);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    handleSelect(s);
                  }}
                >
                  <div className="search-list-prop-heading">
                    {s.description || s.structured_formatting?.main_text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Unit Number Input */}
        {(typeof watchedAddressIncludesUnit !== "undefined"
          ? watchedAddressIncludesUnit
          : showUnit) && (
          <div className="calc-frm-fld form-group small-input">
            <Controller
              name="addressMeta.unitNumber"
              control={control}
              defaultValue={
                getValues ? (getValues("addressMeta.unitNumber") ?? "") : ""
              }
              render={({ field }) => (
                <FloatingInput
                  {...field}
                  placeholder="Unit No."
                  label="Unit No."
                  type="text"
                  className="input-filled"
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </div>
        )}
      </div>

      {/* City, State, Zip */}
      <div className="calc-frm-field full-dvd">
        <div className="calc-frm-fld form-group">
          <Controller
            name="addressMeta.city"
            control={control}
            defaultValue={
              getValues ? (getValues("addressMeta.city") ?? "") : ""
            }
            render={({ field }) => (
              <FloatingInput
                {...field}
                value={
                  typeof watchedCity !== "undefined" ? watchedCity : field.value
                }
                placeholder=""
                label="City"
                type="text"
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group no-m-space">
              <Controller
                name="addressMeta.state"
                control={control}
                defaultValue={
                  getValues ? (getValues("addressMeta.state") ?? "") : ""
                }
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      typeof watchedState !== "undefined"
                        ? watchedState
                        : field.value
                    }
                    placeholder=""
                    label="State"
                    type="text"
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space">
              <Controller
                name="addressMeta.zip"
                control={control}
                defaultValue={
                  getValues ? (getValues("addressMeta.zip") ?? "") : ""
                }
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      typeof watchedZip !== "undefined"
                        ? watchedZip
                        : field.value
                    }
                    placeholder=""
                    label="Zip"
                    type="text"
                    maxLength={5}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      field.onChange(val);
                    }}
                    onBlur={(e) => {
                      try {
                        field.onBlur && field.onBlur(e);
                      } catch (err) {
                        /* ignore */
                      }
                      const val =
                        e && e.target && typeof e.target.value === "string"
                          ? e.target.value.replace(/\D/g, "")
                          : (typeof watchedZip === "string"
                              ? watchedZip.replace(/\D/g, "")
                              : watchedZip) || "";
                      if (val && (val.length === 5 || val.length === 6)) {
                        verifyZip(val);
                      }
                    }}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Closing County */}
      <div className="calc-frm-field full-dvd fsapce-top fsapce-bottom">
        <div className="calc-frm-fld form-group">
          <Controller
            name="addressMeta.closing_county"
            control={control}
            defaultValue={
              getValues ? (getValues("addressMeta.closing_county") ?? "") : ""
            }
            render={({ field }) => (
              <FloatingInput
                {...field}
                value={
                  typeof watchedCounty !== "undefined"
                    ? watchedCounty
                    : field.value
                }
                placeholder=""
                label="Closing County"
                type="text"
              />
            )}
          />
        </div>
        <div className="calc-frm-fld form-group conty-full">
          <button
            className="btn-add-more heading-b"
            onClick={(e) => {
              e.preventDefault();
              openModal({
                title: "Set Closing County",
                size: "smallmodel",
                content: (
                  <SetCountyModal
                    control={control}
                    setValue={setValue}
                    onClose={closeModal}
                    calc="seller"
                    getValues={getValues}
                  />
                ),
              });
            }}
          >
            Set County
          </button>
        </div>
      </div>
    </>
  );
}
